import { ChangeEvent, memo, useCallback, useContext, useEffect, useState } from 'react';

import { Button, Checkbox, Grid, ListItemText, MenuItem } from '@mui/material';
import capitalize from 'lodash/capitalize';
import isArray from 'lodash/isArray';
import { useTranslation } from 'react-i18next';

import { FormDiv, StyledSelect, StyledTextField } from './styled';
import { AppContext } from 'context';
import { preventBodyScrollOnModal } from 'services/preventBodyScrollOnModal';

const getShouldAutoFocus = (selected: string[], options: string[], index: number) => {
    if (selected.length === 0) {
        return index === 0; // Focus first option when none selected
    }

    // Find the first selected option in the options array
    const firstSelectedIndex = options.findIndex((option) => selected.includes(option));
    return index === firstSelectedIndex;
};

const createKeyDownHandler = (currentSelection: string[], updateSelection: (newSelection: string[]) => void) => {
    return (e: React.KeyboardEvent) => {
        if (e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            const target = e.target as HTMLElement;
            const input = target.querySelector('input[type="checkbox"]') as HTMLInputElement;

            if (input) {
                const value = input.value;
                const isSelected = currentSelection.includes(value);
                const newSelection = isSelected
                    ? currentSelection.filter((item) => item !== value)
                    : [...currentSelection, value];
                updateSelection(newSelection);
            }
        }
    };
};

const MainFilterForm = memo(() => {
    const { t } = useTranslation();
    const { foundTags, availableLocations, setFilterType, filterType, contentView, layouts } = useContext(AppContext);

    const tagOptions: {
        value: string;
        label: string;
    }[] = [];
    Array.from(foundTags).forEach((tag) => {
        const _name = (tag.display_name !== null && tag.display_name !== '' ? tag.display_name : tag.key).toLowerCase();
        if (!tagOptions.some((tagOption) => tagOption.value === _name)) {
            tagOptions.push({
                value: _name,
                label: tag.display_name !== null && tag.display_name !== '' ? tag.display_name : tag.key,
            });
        }
    });
    tagOptions.sort((a, b) => {
        if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
        if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
        return 0;
    });

    // const [selectedTag, setSelectedTag] = useState<string[]>(filterType.tags ?? []);
    const [selectedLocation, setSelectedLocation] = useState<string[]>(Array.from(filterType.locations) ?? []);
    const [textField, setTextField] = useState(filterType.textField ?? '');
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    // Layout visibility for filters
    const showTagsFilter = layouts[contentView].filters.includes('TAGS');
    const showLocationsFilter = layouts[contentView].filters.includes('LOCATION');
    const showSearchFilter = layouts[contentView].filters.includes('SEARCH');

    // Effect to manage body overflow when select is open using CSS classes
    useEffect(() => {
        return preventBodyScrollOnModal(isSelectOpen);
    }, [isSelectOpen]);

    const handleChangeTags = useCallback(
        (e: any) => {
            const tagNames: string[] = e.target.value;
            if (tagNames.includes('')) {
                setFilterType({ ...filterType, tags: [] });
            } else {
                setFilterType({ ...filterType, tags: tagNames });
            }
        },
        [filterType]
    );

    const handleChangeLocations = useCallback(
        (e: any) => {
            if (e.target.value.includes('')) {
                setSelectedLocation([]);
                setFilterType({ ...filterType, locations: new Set() });
            } else {
                setSelectedLocation(e.target.value);
                setFilterType({ ...filterType, locations: new Set(e.target.value) });
            }
        },
        [filterType]
    );

    const handleTextField = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setTextField(e.target.value);
    }, []);

    const handleOnSubmit = useCallback(
        (e: ChangeEvent<HTMLFormElement>) => {
            e.preventDefault();
            setFilterType({ ...filterType, textField });
        },
        [filterType, textField]
    );

    const handleOnReset = useCallback(() => {
        setFilterType({ ...filterType, locations: new Set(), tags: [], textField: '', dateRange: [null, null] });
        setSelectedLocation([]);
        setTextField('');
    }, [filterType]);

    // Create key down handlers for tags and locations
    const handleTagsKeyDown = createKeyDownHandler(filterType.tags || [], (newTags) => {
        setFilterType({ ...filterType, tags: newTags });
    });

    const handleLocationsKeyDown = createKeyDownHandler(selectedLocation, (newSelection) => {
        setSelectedLocation(newSelection);
        setFilterType({ ...filterType, locations: new Set(newSelection) });
    });

    if (!showTagsFilter && !showLocationsFilter && !showSearchFilter) {
        return null;
    }

    const sortedLocations = Array.from(availableLocations).sort();

    return (
        <FormDiv>
            {/* create a simple checkbox list */}
            <form role='form' onSubmit={handleOnSubmit} onReset={handleOnReset}>
                <Grid container spacing={3}>
                    {showTagsFilter && (
                        <Grid item xs={6} md={2}>
                            <StyledSelect
                                name='tags'
                                variant='outlined'
                                size='small'
                                value={filterType.tags}
                                multiple
                                onOpen={() => setIsSelectOpen(true)}
                                onClose={() => setIsSelectOpen(false)}
                                MenuProps={{
                                    variant: 'menu',
                                    sx: { maxHeight: 400 },
                                    disablePortal: false,
                                    container: document.getElementById('menu-portal-container'),
                                    onKeyDown: handleTagsKeyDown,
                                    MenuListProps: {
                                        id: 'tags-menu',
                                        role: 'listbox',
                                        'aria-label': t('form.all_tags'),
                                        'aria-multiselectable': 'true',
                                    },
                                }}
                                SelectDisplayProps={{
                                    'aria-label': t('form.all_tags'),
                                }}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (isArray(selected)) {
                                        if (selected.length === 0) {
                                            return <em>{t('form.all_tags')}</em>;
                                        }
                                        let isFirst = true;
                                        const renderString = tagOptions.map((tagOption) => {
                                            if (selected.includes(tagOption.value)) {
                                                const optionString = `${isFirst ? '' : ', '}${tagOption.label}`;
                                                isFirst = false;
                                                return optionString;
                                            } else {
                                                return '';
                                            }
                                        });
                                        return renderString;
                                    }
                                    return null;
                                }}
                                onChange={handleChangeTags}
                            >
                                <MenuItem tabIndex={-1} aria-hidden={true} disabled>
                                    {t('form.all_tags')}
                                </MenuItem>
                                {tagOptions.map((tagOption, index) => {
                                    const isChecked = filterType.tags.includes(tagOption.value);
                                    // Auto-focus first selected option or first option
                                    const shouldAutoFocus = getShouldAutoFocus(
                                        filterType.tags,
                                        tagOptions.map((tag) => tag.value),
                                        index
                                    );

                                    return (
                                        <MenuItem
                                            key={tagOption.value}
                                            value={tagOption.value}
                                            role='option'
                                            aria-selected={isChecked}
                                            autoFocus={shouldAutoFocus}
                                        >
                                            <Checkbox
                                                checked={isChecked}
                                                aria-hidden={true}
                                                tabIndex={-1}
                                                value={tagOption.value}
                                            />
                                            <ListItemText primary={tagOption.label} />
                                        </MenuItem>
                                    );
                                })}
                            </StyledSelect>
                        </Grid>
                    )}
                    {showLocationsFilter && (
                        <Grid item xs={6} md={2}>
                            <StyledSelect
                                variant='outlined'
                                size='small'
                                onChange={handleChangeLocations}
                                multiple
                                value={selectedLocation}
                                data-testid='locationSelect'
                                onOpen={() => setIsSelectOpen(true)}
                                onClose={() => setIsSelectOpen(false)}
                                displayEmpty
                                SelectDisplayProps={{
                                    'aria-label': t('form.any_city'),
                                }}
                                MenuProps={{
                                    variant: 'menu',
                                    sx: { maxHeight: 400 },
                                    onKeyDown: handleLocationsKeyDown,
                                    MenuListProps: {
                                        role: 'listbox',
                                        'aria-label': t('form.any_city'),
                                        'aria-multiselectable': 'true',
                                        autoFocusItem: true,
                                    },
                                }}
                                renderValue={(selected) => {
                                    if (isArray(selected)) {
                                        if (selected.length === 0) {
                                            return <em>{t('form.any_city')}</em>;
                                        }
                                        return selected.join(', ');
                                    }
                                    return null;
                                }}
                            >
                                <MenuItem tabIndex={-1} aria-hidden={true} disabled>
                                    {t('form.any_city')}
                                </MenuItem>
                                {sortedLocations.map((location, index) => {
                                    const isSelected = selectedLocation.includes(location);
                                    // Auto-focus first selected option or first option
                                    const shouldAutoFocus = getShouldAutoFocus(
                                        selectedLocation,
                                        sortedLocations,
                                        index
                                    );

                                    return (
                                        <MenuItem
                                            key={location}
                                            value={location}
                                            role='option'
                                            aria-selected={isSelected}
                                            autoFocus={shouldAutoFocus}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                aria-hidden={true}
                                                tabIndex={-1}
                                                value={location}
                                            />
                                            <ListItemText primary={capitalize(location)} />
                                        </MenuItem>
                                    );
                                })}
                            </StyledSelect>
                        </Grid>
                    )}
                    {showSearchFilter && (
                        <Grid item xs={12} md={3}>
                            <StyledTextField
                                value={textField}
                                onChange={handleTextField}
                                type='text'
                                size='small'
                                data-testid='textfield'
                                placeholder={t('form.search_for_event')}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={5}>
                        {showSearchFilter && (
                            <Button sx={{ mr: 1.8 }} data-testid='submitButton' variant='contained' type='submit'>
                                {t('form.search')}
                            </Button>
                        )}
                        <Button data-testid='resetButton' variant='contained' color='inherit' type='reset'>
                            {t('form.reset')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </FormDiv>
    );
});

export default MainFilterForm;
